import { ExternalLink } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';

import { BlocksCard } from './BlocksCard';
import { DifficultyCard } from './DifficultyCard';
import { FeesCard } from './FeesCard';
import { HashrateCard } from './HashrateCard';
import { MempoolCard } from './MempoolCard';
import { Badge } from '@/components/ui/badge';
import {
  CHAINS,
  formatInt,
  shortHash,
  type BlockInfo,
  type ChainId,
  type DifficultyAdjustment,
  type HashrateResponse,
  type MempoolInfo,
  type RecommendedFees,
} from '@/lib/mempool';

export interface ChainQueries {
  blocks: UseQueryResult<BlockInfo[]>;
  difficulty: UseQueryResult<DifficultyAdjustment>;
  hashrate: UseQueryResult<HashrateResponse>;
  mempool: UseQueryResult<MempoolInfo>;
  fees: UseQueryResult<RecommendedFees>;
}

export function ChainPanel({ chainId, queries }: { chainId: ChainId; queries: ChainQueries }) {
  const chain = CHAINS[chainId];
  const tip = queries.blocks.data?.[0];

  return (
    <section
      aria-label={`${chain.name} (${chain.shortName}) chain statistics`}
      className={`relative isolate overflow-hidden rounded-2xl border bg-card/40 ${chain.classes.panelRing}`}
    >
      {/* Accent gradient wash */}
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-x-0 top-0 -z-10 h-40 bg-gradient-to-b ${chain.classes.gradient}`}
      />

      <header className="flex flex-wrap items-center gap-3 px-5 pt-5 sm:px-6">
        <span className={`size-2.5 rounded-full ${chain.classes.dot}`} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold tracking-tight">
              {chain.name} <span className={chain.classes.text}>({chain.shortName})</span>
            </h2>
            <Badge variant="outline" className={`font-mono ${chain.classes.softBadge}`}>
              {tip ? `#${formatInt(tip.height)}` : 'syncing…'}
            </Badge>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{chain.tagline}</p>
        </div>
        <a
          href={chain.explorerBase}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {chain.explorerBase.replace('https://', '')}
          <ExternalLink className="size-3" />
        </a>
      </header>

      {tip && (
        <p className="px-5 pt-2 font-mono text-[11px] text-muted-foreground sm:px-6">
          tip {shortHash(tip.id, 12, 8)}
        </p>
      )}

      <div className="grid gap-4 p-4 sm:p-5 md:grid-cols-2">
        <HashrateCard chainId={chainId} query={queries.hashrate} />
        <DifficultyCard chainId={chainId} query={queries.difficulty} />
        <MempoolCard chainId={chainId} query={queries.mempool} />
        <FeesCard chainId={chainId} query={queries.fees} />
        <div className="md:col-span-2">
          <BlocksCard chainId={chainId} query={queries.blocks} />
        </div>
      </div>
    </section>
  );
}
